import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import type { SiteRagReport } from '../../../types/siteRag';
import { Button } from '../../ui/button';
import { reportSummaryPath } from '../../../lib/siteNavigation';
import { screeningSummaryEligibility } from '../../../lib/screeningSummary/eligibility';
import { useStore } from '../../../store';
import { downloadScreeningSummary, prebuildScreeningSummary } from './summaryArtifactActions';
import { ReportSummaryActions } from './ReportSummaryActions';
export function ScreeningSummaryActions({ report, siteId, onOpenSummary }: { report: SiteRagReport; siteId: string; onOpenSummary?: (reportId: string) => void }) {
  return (report.reportType ?? 'feasibility') === 'feasibility' ? <FrozenScreeningSummaryActions report={report} siteId={siteId} onOpenSummary={onOpenSummary} /> : <ReportSummaryActions report={report} siteId={siteId} onOpenSummary={onOpenSummary} />;
}

function FrozenScreeningSummaryActions({ report, siteId, onOpenSummary }: { report: SiteRagReport; siteId: string; onOpenSummary?: (reportId: string) => void }) {
  const navigate = useNavigate();
  const companyId = useStore(s => s.companyId);
  const [pending, setPending] = useState(false);
  const eligible = screeningSummaryEligibility(report).ok;
  const actionIdentity = `${companyId ?? ''}::${siteId}::${report.id}::${report.reportRevision?.revisionId ?? ''}`;
  const actionIdentityRef = React.useRef(actionIdentity);
  const reqSeqRef = React.useRef(0);
  const lifecycleEpochRef = React.useRef(0);
  const isMountedRef = React.useRef(true);
  if (actionIdentityRef.current !== actionIdentity) {
    actionIdentityRef.current = actionIdentity;
    lifecycleEpochRef.current += 1;
    reqSeqRef.current += 1;
  }
  const isActionRequestCurrent = (
    requestIdentity: string,
    requestSeq: number,
    requestEpoch: number,
  ): boolean =>
    isMountedRef.current
    && lifecycleEpochRef.current === requestEpoch
    && actionIdentityRef.current === requestIdentity
    && reqSeqRef.current === requestSeq;
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      lifecycleEpochRef.current += 1;
      reqSeqRef.current += 1;
    };
  }, []);
  useEffect(() => {
    setPending(false);
    if (eligible) prebuildScreeningSummary(report, undefined, companyId ?? null);
  }, [eligible, actionIdentity]);
  if (!eligible) return null;
  return <>
    <Button size="sm" variant="outline" data-testid="findings-summary-feasibility" onClick={() => onOpenSummary ? onOpenSummary(report.id) : navigate(reportSummaryPath(siteId, report.id))}><FileText aria-hidden />Summary</Button>
    <Button size="sm" variant="ghost" data-testid="findings-summary-pdf-feasibility" aria-label="Download screening summary PDF" disabled={pending} onClick={() => {
      const requestIdentity = actionIdentity;
      const requestEpoch = lifecycleEpochRef.current;
      const requestSeq = ++reqSeqRef.current;
      setPending(true);
      void downloadScreeningSummary(report, {
        companyId: companyId ?? null,
        shouldSave: () => isActionRequestCurrent(requestIdentity, requestSeq, requestEpoch),
      }).catch(e => {
        if (isActionRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
          toast.error((e as Error).message);
        }
      }).finally(() => {
        if (isActionRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
          setPending(false);
        }
      });
    }}><Download aria-hidden />{pending ? 'Building…' : 'PDF'}</Button>
  </>;
}
